/**
 * The HTTP client every API call goes through.
 *
 * It owns three things that would otherwise be repeated in every caller:
 * attaching the access token, refreshing it exactly once when it expires, and
 * turning an error response into a typed error the UI can render in Romanian.
 */

export interface ApiErrorBody {
  statusCode: number;
  error: string;
  message: string | string[];
  requestId?: string;
  path?: string;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly requestId?: string,
    /** Field-level messages from validation, when the server sent a list. */
    readonly details?: string[],
  ) {
    super(message);
    this.name = 'ApiError';
  }

  get isUnauthorized() {
    return this.status === 401;
  }
  get isForbidden() {
    return this.status === 403;
  }
  get isNotFound() {
    return this.status === 404;
  }
  get isConflict() {
    return this.status === 409;
  }
  /** Worth offering a retry button for; a 4xx is not. */
  get isRetryable() {
    return this.status >= 500 || this.status === 429 || this.status === 408;
  }
}

const BASE = '/api/v1';
const REFRESH_STORAGE_KEY = 'df.refresh';

/**
 * The access token is held in memory only.
 *
 * Keeping it out of localStorage means a script injected into the page cannot
 * read it from storage — it survives a reload only by exchanging the refresh
 * token, which is the trade being made deliberately.
 */
let accessToken: string | null = null;

/** Called when the session ends for good, so the app can send the user to /login. */
let onSessionLost: (() => void) | null = null;

export const auth = {
  setAccessToken(token: string | null) {
    accessToken = token;
  },
  getAccessToken() {
    return accessToken;
  },
  setRefreshToken(token: string | null) {
    try {
      if (token) localStorage.setItem(REFRESH_STORAGE_KEY, token);
      else localStorage.removeItem(REFRESH_STORAGE_KEY);
    } catch {
      // Private browsing, or storage disabled. The session then lasts until
      // the access token expires, which is degraded but not broken.
    }
  },
  getRefreshToken(): string | null {
    try {
      return localStorage.getItem(REFRESH_STORAGE_KEY);
    } catch {
      return null;
    }
  },
  clear() {
    accessToken = null;
    this.setRefreshToken(null);
  },
  onSessionLost(fn: () => void) {
    onSessionLost = fn;
  },
};

/**
 * Who hears about a write that succeeded.
 *
 * A write changes what other screens show, and the screen that made it does
 * not know which ones: a booking made from the calendar, the assistant's card
 * or the confirmation queue all change the dashboard's counts. Hearing it here,
 * once, beats every caller remembering every key (`lib/write-invalidation.ts`).
 */
type WriteListener = (method: string, path: string) => void;
const writeListeners = new Set<WriteListener>();

/** Subscribes to successful non-GET requests. Returns the unsubscribe. */
export function onWriteSucceeded(listener: WriteListener): () => void {
  writeListeners.add(listener);
  return () => {
    writeListeners.delete(listener);
  };
}

/**
 * In-flight refresh, shared by every request that got a 401 at the same time.
 *
 * Without this, a screen making six parallel requests would fire six refreshes;
 * the first rotates the token and the other five present a spent one — which
 * the server correctly treats as a stolen token and responds to by ending
 * every session. The user gets logged out for loading a page.
 */
let refreshInFlight: Promise<boolean> | null = null;

export async function refreshAccessToken(): Promise<boolean> {
  const refreshToken = auth.getRefreshToken();
  if (!refreshToken) return false;

  refreshInFlight ??= (async () => {
    try {
      const res = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!res.ok) return false;
      const data = (await res.json()) as { accessToken: string; refreshToken: string };
      auth.setAccessToken(data.accessToken);
      auth.setRefreshToken(data.refreshToken);
      return true;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  /** Return the `Response` untouched, for a file the browser will save. */
  raw?: boolean;
  body?: unknown;
  /** Set for the calls that must not trigger a refresh (login, refresh itself). */
  skipAuth?: boolean;
  query?: Record<string, string | number | boolean | null | undefined>;
  /**
   * După câte milisecunde se renunță la o citire (T7). Doar pentru citirile
   * care trebuie să răspundă repede — nu pentru încărcări sau exporturi, care
   * durează cât durează. Expirarea devine o eroare 408, cu „Reîncearcă".
   */
  timeoutMs?: number;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, skipAuth, query, headers, raw, timeoutMs, ...init } = options;

  const url = new URL(`${BASE}${path}`, window.location.origin);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, String(v));
    }
  }

  // FormData carries its own encoding, and its multipart boundary is generated
  // by the browser. Setting Content-Type by hand would omit that boundary and
  // the server would reject the body; JSON.stringify would quietly turn it
  // into "{}" — an upload that succeeds with an empty file.
  const isForm = typeof FormData !== 'undefined' && body instanceof FormData;

  const send = async (): Promise<Response> => {
    const signals = [init.signal, timeoutMs ? AbortSignal.timeout(timeoutMs) : null].filter(
      (x): x is AbortSignal => Boolean(x),
    );
    try {
      return await fetch(url.toString(), {
        ...init,
        signal: signals.length > 1 ? AbortSignal.any(signals) : signals[0],
        headers: {
          ...(body !== undefined && !isForm ? { 'Content-Type': 'application/json' } : {}),
          ...(!skipAuth && accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          ...headers,
        },
        body: body === undefined ? undefined : isForm ? (body as FormData) : JSON.stringify(body),
      });
    } catch (err) {
      // By name, not `instanceof DOMException`: the error can come from another realm.
      if ((err as { name?: string } | null)?.name === 'TimeoutError') {
        throw new ApiError(
          408,
          'RequestTimeout',
          'Serverul nu a răspuns la timp. Verifică legătura și reîncearcă.',
        );
      }
      throw err;
    }
  };

  let response = await send();

  // One retry, and only for an expired access token. Retrying a 403 or a 500
  // would just repeat the same failure.
  if (response.status === 401 && !skipAuth) {
    if (await refreshAccessToken()) {
      response = await send();
    } else {
      auth.clear();
      onSessionLost?.();
    }
  }

  if (!response.ok) throw await toApiError(response);

  const method = (init.method ?? 'GET').toUpperCase();
  if (method !== 'GET' && method !== 'HEAD') {
    for (const listener of writeListeners) {
      try {
        listener(method, path);
      } catch {
        // A listener refreshes other screens; it never fails this write.
      }
    }
  }

  // A caller that asked for the raw response is downloading a file: the body
  // is a CSV or a large JSON, and parsing it here would only mean serialising
  // it straight back out.
  if (raw) return response as T;

  if (response.status === 204) return undefined as T;

  /**
   * A 200 with nothing in it.
   *
   * Nest sends an empty body for a handler that answers `null` — which is what
   * "this appointment consumed nothing" looks like — and `response.json()`
   * throws on empty input. The query then fails, React Query keeps the last
   * good value, and the screen goes on showing data that no longer exists:
   * the materials panel kept listing a consumption that had just been
   * reversed. Treated as `null`, which is what the server meant.
   */
  const text = await response.text();
  if (text.trim() === '') return null as T;
  return JSON.parse(text) as T;
}

/** The fields every error body has; anything else is the endpoint's own. */
const ERROR_ENVELOPE = new Set([
  'statusCode',
  'error',
  'message',
  'requestId',
  'path',
  'timestamp',
]);

async function toApiError(response: Response): Promise<ApiError> {
  let body: ApiErrorBody | null = null;
  try {
    body = (await response.json()) as ApiErrorBody;
  } catch {
    // A proxy error page, or an empty body.
  }

  const raw = body?.message;
  const details = Array.isArray(raw) ? raw : undefined;
  const message = Array.isArray(raw) ? raw[0] : (raw ?? fallbackMessage(response.status));

  const err = new ApiError(
    response.status,
    body?.error ?? 'Error',
    message,
    body?.requestId,
    details,
  );
  // Whatever else the server attached — the bookings a slot clashes with, the
  // plan rows another booking holds — travels on the error, for the screen
  // that asked. Without it `clashes` never arrived, and the booking dialog
  // listed nothing under „Interval ocupat”.
  if (body) {
    for (const [key, value] of Object.entries(body)) {
      if (!ERROR_ENVELOPE.has(key) && !(key in err)) {
        (err as unknown as Record<string, unknown>)[key] = value;
      }
    }
  }
  return err;
}

/** Romanian, because it is shown to the user when the server sent nothing usable. */
function fallbackMessage(status: number): string {
  if (status === 0) return 'Fără conexiune la server.';
  if (status === 401) return 'Sesiune expirată. Autentifică-te din nou.';
  if (status === 403) return 'Nu ai permisiunea necesară.';
  if (status === 404) return 'Resursa nu a fost găsită.';
  if (status === 429) return 'Prea multe cereri. Încearcă din nou în câteva momente.';
  if (status >= 500) return 'Eroare de server. Încearcă din nou.';
  return 'A apărut o eroare.';
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  delete: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'DELETE' }),

  /**
   * Fetch a file and hand it to the browser to save.
   *
   * It goes through `request` rather than a plain link because the endpoint
   * needs the access token, and a link cannot carry a header — and because the
   * token may need refreshing first, which this gets for free.
   *
   * The filename comes from the server's `Content-Disposition` when it sends
   * one, so the name in the download folder is the name the API chose.
   */
  download: async (path: string, fallbackName: string, options?: RequestOptions) => {
    const response = await request<Response>(path, { ...options, method: 'GET', raw: true });
    const disposition = response.headers.get('Content-Disposition') ?? '';
    const named = /filename="?([^";]+)"?/i.exec(disposition)?.[1];
    const url = URL.createObjectURL(await response.blob());
    try {
      const a = document.createElement('a');
      a.href = url;
      a.download = named ?? fallbackName;
      document.body.append(a);
      a.click();
      a.remove();
    } finally {
      // Revoking immediately after the click is safe: the browser has already
      // taken its own reference to the blob.
      URL.revokeObjectURL(url);
    }
  },
};
